import { Alert } from '@mui/material'
import { Stack } from '@mui/system'
import { useTranslate } from '@tolgee/react'
import { ConfirmationDialog } from '@tom-ui/ui'
import { YardBlockDto, YardBlockType } from '@yard/app/api'
import { YardPositionBlockRange } from '@yard/components/YardPositionSelector/models/yard-position-block-range.model'
import { YardPositionRangeSelector } from '@yard/components/YardPositionSelector/YardPositionRangeSelector'
import { getSlotCountByDestination } from '@yard/utils/yard-block.util'
import { observer } from 'mobx-react-lite'
import { useCallback, useMemo, useState } from 'react'

interface Props {
  isOpen: boolean
  yardBlocks: YardBlockDto[]
  position?: YardPositionBlockRange
  hasError?: boolean
  onPositionChange: (position?: YardPositionBlockRange) => void
  onClose: () => void
  onConfirm: () => void
}
export const AllocationRuleDestinationDialog = observer(
  ({ isOpen, yardBlocks, position, hasError, onClose, onConfirm, onPositionChange }: Props) => {
    const { t } = useTranslate()

    const [count, setCount] = useState(0)
    const [destination, setDestination] = useState<YardPositionBlockRange | undefined>(position)
    const isFlexibleBlock = useMemo(
      () =>
        yardBlocks.find(b => b.id === destination?.blockId)?.type !== YardBlockType.FlexibleBlock,
      [destination, yardBlocks],
    )

    const changePosition = useCallback(
      (destination?: YardPositionBlockRange) => {
        let newCount = 0

        if (destination) {
          newCount = getSlotCountByDestination(
            yardBlocks,
            destination.blockId,
            destination.minBay,
            destination.maxBay,
            destination.minRow,
            destination.maxRow,
          )
        }

        setCount(newCount)
        setDestination(destination)
        onPositionChange(destination)
      },
      [onPositionChange, yardBlocks],
    )

    return (
      <ConfirmationDialog
        open={isOpen}
        title={t('defineDestination', 'Define destination')}
        primaryActionText={t('yesConfirm', 'Yes, confirm')}
        closeLabel={t('noCancel', 'No, cancel')}
        onClose={() => {
          onClose()
          changePosition()
        }}
        onConfirm={() => {
          onConfirm()
          changePosition()
        }}
        maxWidth='md'
      >
        <Stack paddingY={2} gap={2}>
          <YardPositionRangeSelector
            position={position}
            hasError={hasError}
            onChange={newPosition => changePosition(newPosition)}
          />
          {destination && isFlexibleBlock && (
            <Alert severity={count === 0 ? 'warning' : 'info'} data-cy='destination-dialog-info'>
              {t('availableSlotsCount', '{count} available slots for the selected destination', {
                count,
              })}
            </Alert>
          )}
        </Stack>
      </ConfirmationDialog>
    )
  },
)
